/**
 * Dynamic assessments module — the shared question renderer plus the
 * server-driven dynamic-assessment flow that sits on top of it.
 *
 * The module is independent by construction: it imports nothing from
 * `@/modules/rulesEngine`, `@/modules/api` or `@/modules/analytics`. Everything
 * arrives through `DynamicAssessmentProvider` — `DynamicAssessmentScreen` reads the
 * `DynamicAssessmentHost` contract below from its `host` prop. That's what stops
 * rulesEngine → analytics (Datadog + PostHog) → api (expo-secure-store) → chat
 * (stream-chat) reaching the legacy screens that only want `AssessmentContent`, or a
 * host app that has no onboarding rules engine at all.
 *
 * Both entry points need a `DynamicAssessmentProvider`; they differ in how much of it:
 *   - `DynamicAssessmentFlow` — needs `apiClient` (and optionally `analytics`).
 *     Reports completion through `onDone`; no `host` required.
 *   - `DynamicAssessmentScreen` — adds flag gating + outcome routing, so it also
 *     requires `host`.
 *
 * See `docs/packaging-plan.md` §4.
 */

// Shared assessment rendering — used by the dynamic flow AND the three legacy paths
// (AssignedScreenersScreen, PreDiabetesScreen, PhqGadScreen).
export { AssessmentContent } from './components/AssessmentContent/AssessmentContent';
export {
  FREE_TEXT_QUESTION_TYPES,
  type Answer,
  type Answers,
  type Conditions,
  type QuestionChoice,
  type AssessmentQuestion,
  type RepeatingListColumn,
  type RepeatingListRow,
} from './types/assessmentQuestion';
export { toSingleAnswerValue } from './utils/toSingleAnswerValue';

// Dynamic assessment — the server-driven flow walker.
export {
  DynamicAssessmentFlow,
  type DynamicAssessmentDoneReason,
  type DynamicAssessmentResult,
} from './components/DynamicAssessmentFlow/DynamicAssessmentFlow';
export { DynamicAssessmentScreen } from './components/DynamicAssessmentScreen/DynamicAssessmentScreen';
export {
  useDynamicAssessmentSession,
  type DynamicAssessmentContent,
} from './hooks/useDynamicAssessmentSession';
export { toDynamicAssessmentQuestions } from './utils/toDynamicAssessmentQuestions';

// Injection — what an embedding app supplies. The paired context consumers in
// `hooks/` (`useDynamicAssessmentApiClient`, `useDynamicAssessmentAnalytics`, `useDynamicAssessmentHost`)
// and the raw `DynamicAssessmentContext` are deliberately NOT exported: they're internal
// plumbing. The app-side hook that BUILDS the host is `useOnboardingSurveyHost`
// in `@/modules/rulesEngine`.
export { DynamicAssessmentProvider } from './contexts/DynamicAssessmentContext';
export type { DynamicAssessmentHost } from './types/dynamicAssessmentHost';
export type { DynamicAssessmentAnalytics } from './types/dynamicAssessmentAnalytics';
export type { DynamicAssessmentApiClient } from './types/dynamicAssessmentApiClient';

// The wire shapes those endpoints speak. In `types/` rather than beside the
// calls so the pure layers (`utils/`, `hooks/`) can name them without importing
// the network module — see types/dynamicAssessment.ts.
export type {
  DynamicFlowItem,
  DynamicFlowStatus,
  DynamicNodeType,
  DynamicSession,
  DynamicSessionStatus,
  DynamicTerminalOutcome,
  DynamicUiPayload,
} from './types/dynamicAssessment';

// The module's own network layer. Exported so callers can type an injected client
// and, if needed, drive the endpoints directly. `DynamicOnboardingFlowKey` ships
// from here because it's derived from the `DYNAMIC_ONBOARDING_FLOW_KEYS` value.
export {
  PATHS as DYNAMIC_ASSESSMENT_PATHS,
  DYNAMIC_ONBOARDING_FLOW_KEYS,
  fetchDynamicFlows,
  startOrResumeSession,
  transitionSession,
  type DynamicOnboardingFlowKey,
} from './api/dynamicAssessments';
